import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import DeletionRequestsPanel from './DeletionRequestsPanel';
import { 
  Users, Plus, Edit2, Trash2, Shield, Key, Search, 
  Loader2, X, Check, Eye, EyeOff, UserCog, Mail
} from 'lucide-react';
import { HeaderSkeleton, TableSkeleton } from './skeletons/Skeletons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { formatDate } from '../lib/dateUtils';
import MobileDataCard from './MobileDataCard';

const PERMISSION_LABELS = {
  dashboard: { label: 'Dashboard', description: 'View main dashboard' },
  tasks: { label: 'Tasks', description: 'Manage tasks and assignments' },
  calendars: { label: 'Calendars', description: 'View and manage calendars' },
  human_resources: { label: 'Human Resources', description: 'HR management, hiring, leave' },
  organisation: { label: 'Organisation', description: 'Departments and org structure' },
  clients: { label: 'Clients', description: 'Client management' },
  helpdesk: { label: 'Help Desk', description: 'Support tickets' },
  logs: { label: 'Audit Logs', description: 'View system logs' },
  settings: { label: 'Settings', description: 'System settings' },
  user_management: { label: 'User Management', description: 'Manage admin users' }
};

const ROLE_COLORS = {
  superadmin: 'bg-red-100 text-red-800 border-red-200',
  admin: 'bg-blue-100 text-blue-800 border-blue-200',
  hr: 'bg-purple-100 text-purple-800 border-purple-200',
  manager: 'bg-green-100 text-green-800 border-green-200'
};

export default function AdminUserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'admin',
    permissions: []
  });
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchPermissions();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/api/admin/users');
      setUsers(response.data || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You do not have permission to manage users');
      } else {
        toast.error('Failed to load admin users');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await adminApi.get('/api/admin/users/permissions');
      setAvailableRoles(response.data.roles || []);
      setAvailablePermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Failed to load permissions');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.post('/api/admin/users', formData);
      toast.success('Admin user created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setSaving(true);
    try {
      await adminApi.put(`/api/admin/users/${selectedUser._id}`, {
        full_name: formData.full_name,
        role: formData.role,
        permissions: formData.permissions,
        is_active: formData.is_active
      });
      toast.success('Admin user updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await adminApi.delete(`/api/admin/users/${selectedUser._id}`);
      toast.success('Admin user deleted successfully');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newPassword) return;
    setSaving(true);
    try {
      await adminApi.put(`/api/admin/users/${selectedUser._id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setShowResetPasswordModal(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'admin',
      permissions: []
    });
    setSelectedUser(null);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      full_name: user.full_name,
      role: user.role || 'admin',
      permissions: user.permissions || [],
      is_active: user.is_active !== false
    });
    setShowEditModal(true);
  };

  const openResetPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetPasswordModal(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const togglePermission = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isSuperAdmin = (user) => user.email === 'admin@halfeatendonut.in' || user.role === 'superadmin';

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="admin-user-management-loading">
        <HeaderSkeleton />
        <TableSkeleton rows={6} columns={5} />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="admin-user-management">
      <DeletionRequestsPanel />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-2xl font-bold text-[#1a1a2e] flex items-center gap-3">
            <UserCog className="w-8 h-8 text-pink-500" />
            User Management
          </h1>
          <p className="text-neutral-600 mt-1">Manage admin users and their permissions</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="bg-gradient-to-r from-[#FF3366] to-[#FF3366] hover:from-pink-600 hover:to-pink-700"
          data-testid="create-admin-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Admin User
        </Button>
      </div>

      {/* Search */}
      <div className="glass-card p-4 mb-6">
        <div className="relative">
          <Search className="w-5 h-5 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            type="text"
            placeholder="Search by name, email, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-admin-users"
          />
        </div>
      </div>

      {/* Users List */}
      <div className="soft-card overflow-hidden">
        {/* Mobile card list — swipeable cards on phones (<md) */}
        <div className="md:hidden p-3 space-y-2" data-testid="admin-users-mobile-list">
          {filteredUsers.length === 0 ? (
            <div className="py-10 text-center text-sm text-neutral-500">No admin users found</div>
          ) : filteredUsers.map((user) => {
            const badges = [
              { label: user.role || 'admin', tone: user.role === 'superadmin' ? 'red' : user.role === 'admin' ? 'blue' : 'fuchsia' },
              { label: user.is_active !== false ? 'Active' : 'Inactive', tone: user.is_active !== false ? 'emerald' : 'red' },
            ];
            const actions = [
              { label: 'Reset', icon: Key, onClick: (e) => { e.stopPropagation(); openResetPasswordModal(user); }, tone: 'amber', testId: `mob-reset-${user._id}` },
            ];
            if (!isSuperAdmin(user)) {
              actions.push({ label: 'Delete', icon: Trash2, onClick: (e) => { e.stopPropagation(); openDeleteDialog(user); }, tone: 'red', testId: `mob-del-${user._id}` });
            }
            return (
              <MobileDataCard
                key={user._id}
                testId={`admin-user-card-${user._id}`}
                title={user.full_name || 'N/A'}
                subtitle={user.email}
                badges={badges}
                rows={[
                  { label: 'Permissions', value: (user.permissions || []).length > 0 ? `${user.permissions.length} granted` : 'None' },
                  { label: 'Last Login', value: user.last_login ? formatDate(user.last_login) : 'Never' },
                ]}
                onTap={!isSuperAdmin(user) ? () => openEditModal(user) : undefined}
                actions={actions}
              />
            );
          })}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full" data-testid="admin-users-table">
            <thead className="bg-white/40 border-b border-white/40">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-semibold text-neutral-600 uppercase">User</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-neutral-600 uppercase">Role</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-neutral-600 uppercase">Permissions</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-neutral-600 uppercase">Status</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-neutral-600 uppercase">Last Login</th>
                <th className="text-right px-6 py-4 text-xs font-semibold text-neutral-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/30">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-neutral-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                    <p>No admin users found</p>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user._id} className="hover:bg-white/60" data-testid={`admin-user-row-${user._id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                          <span className="text-pink-600 font-semibold">
                            {user.full_name?.charAt(0) || user.email?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-[#1a1a2e]">{user.full_name || 'N/A'}</p>
                          <p className="text-sm text-neutral-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${ROLE_COLORS[user.role] || 'bg-neutral-100 text-[#1a1a2e]'}`}>
                        <Shield className="w-3 h-3" />
                        {user.role || 'admin'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(user.permissions || []).slice(0, 3).map(perm => (
                          <span key={perm} className="px-2 py-0.5 bg-neutral-100 text-neutral-600 rounded text-xs">
                            {PERMISSION_LABELS[perm]?.label || perm}
                          </span>
                        ))}
                        {(user.permissions || []).length > 3 && (
                          <span className="px-2 py-0.5 bg-neutral-100 text-neutral-600 rounded text-xs">
                            +{user.permissions.length - 3} more
                          </span>
                        )}
                        {(!user.permissions || user.permissions.length === 0) && (
                          <span className="text-xs text-neutral-400">No permissions</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                        user.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-500">
                      {user.last_login ? formatDate(user.last_login) : 'Never'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openResetPasswordModal(user)}
                          className="p-2 text-neutral-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-xl transition-colors"
                          title="Reset Password"
                          data-testid={`reset-password-${user._id}`}
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-2 text-neutral-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
                          title="Edit User"
                          disabled={isSuperAdmin(user)}
                          data-testid={`edit-user-${user._id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteDialog(user)}
                          className="p-2 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                          title="Delete User"
                          disabled={isSuperAdmin(user)}
                          data-testid={`delete-user-${user._id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-pink-500" />
              Create Admin User
            </DialogTitle>
            <DialogDescription>
              Add a new admin user with specific permissions
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@company.com"
                required
                data-testid="create-user-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  required
                  data-testid="create-user-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="John Doe"
                required
                data-testid="create-user-name"
              />
            </div>
            <div>
              <Label htmlFor="role">Role *</Label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-3 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                data-testid="create-user-role"
              >
                {availableRoles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Permissions</Label>
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border border-white/40 rounded-xl p-3">
                {availablePermissions.map(perm => (
                  <div key={perm} className="flex items-center gap-3">
                    <Checkbox
                      id={`perm-${perm}`}
                      checked={formData.permissions.includes(perm)}
                      onCheckedChange={() => togglePermission(perm)}
                    />
                    <label htmlFor={`perm-${perm}`} className="text-sm cursor-pointer flex-1">
                      <span className="font-medium">{PERMISSION_LABELS[perm]?.label || perm}</span>
                      <span className="text-neutral-500 ml-2 text-xs">{PERMISSION_LABELS[perm]?.description}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-pink-500 hover:bg-pink-600">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-500" />
              Edit Admin User
            </DialogTitle>
            <DialogDescription>
              Update user details and permissions
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input value={formData.email} disabled className="bg-white/40" />
            </div>
            <div>
              <Label htmlFor="edit_full_name">Full Name *</Label>
              <Input
                id="edit_full_name"
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="John Doe"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_role">Role *</Label>
              <select
                id="edit_role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-3 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
              >
                {availableRoles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Permissions</Label>
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border border-white/40 rounded-xl p-3">
                {availablePermissions.map(perm => (
                  <div key={perm} className="flex items-center gap-3">
                    <Checkbox
                      id={`edit-perm-${perm}`}
                      checked={formData.permissions.includes(perm)}
                      onCheckedChange={() => togglePermission(perm)}
                    />
                    <label htmlFor={`edit-perm-${perm}`} className="text-sm cursor-pointer flex-1">
                      <span className="font-medium">{PERMISSION_LABELS[perm]?.label || perm}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <label htmlFor="is_active" className="text-sm cursor-pointer">
                <span className="font-medium">Active</span>
                <span className="text-neutral-500 ml-2 text-xs">User can login</span>
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-500 hover:bg-blue-600">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-yellow-500" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="new_password">New Password *</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowResetPasswordModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-yellow-500 hover:bg-yellow-600">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                Reset Password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Admin User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.email}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={saving}
              className="bg-red-500 hover:bg-red-600"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
